import imageCompression from 'browser-image-compression';
import { api } from './api';
import { IMAGE_PRESETS, type ImageKind } from './image-presets';
import type { OrderAttachment, OrderAttachmentKind, UploadedImage } from '../types';

/** Совпадает с `MAX_IMAGE_BYTES` в `apps/api/src/images/image.dto.ts`. */
const MAX_UPLOAD_BYTES = 1_500_000;

export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp';

/**
 * Заранее согласованный набор документов, тот же, что и на сервере:
 * `ALLOWED_DOCUMENT_MIME_TYPES` в `apps/api/src/documents/document.dto.ts`.
 */
export const ACCEPTED_DOCUMENT_TYPES = 'application/pdf,application/zip';

export const ACCEPTED_ATTACHMENT_TYPES = `${ACCEPTED_IMAGE_TYPES},${ACCEPTED_DOCUMENT_TYPES}`;

/** Совпадает с `MAX_DOCUMENT_BYTES`: документ уходит как есть, без сжатия. */
const MAX_DOCUMENT_BYTES = 5_000_000;

export class ImageUploadError extends Error {}

/** Картинку сервер пережмёт, документ — нет: и путь загрузки у них разный. */
export function isDocument(file: Blob): boolean {
  return ACCEPTED_DOCUMENT_TYPES.split(',').includes(file.type);
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Кадрирование выполняет canvas: `react-easy-crop` отдаёт только прямоугольник
 * в пикселях исходника, вырезать его — задача вызывающего кода.
 */
export async function cropToBlob(source: string, area: CropArea, rotation = 0): Promise<Blob> {
  const image = await loadImage(source);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(area.width));
  canvas.height = Math.max(1, Math.round(area.height));

  const context = canvas.getContext('2d');
  if (!context) {
    throw new ImageUploadError('Браузер не дал обработать изображение');
  }

  if (rotation === 0) {
    context.drawImage(
      image,
      area.x,
      area.y,
      area.width,
      area.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  } else {
    drawRotated(context, image, area, rotation, canvas);
  }

  return canvasToBlob(canvas);
}

/**
 * Сжатие идёт в Web Worker: на телефоне пережатие снимка в главном потоке
 * заметно подвешивает интерфейс. Сервер потом всё равно приводит картинку к
 * своему пресету, здесь задача — не гнать по сети лишние мегабайты.
 */
export async function uploadImage(file: Blob, kind: ImageKind): Promise<UploadedImage> {
  const preset = IMAGE_PRESETS[kind];

  let compressed: Blob;
  try {
    compressed = await imageCompression(toFile(file), {
      maxWidthOrHeight: preset.maxSide,
      maxSizeMB: preset.maxSizeMb,
      fileType: 'image/webp',
      useWebWorker: true,
      preserveExif: false,
    });
  } catch {
    throw new ImageUploadError('Не удалось подготовить изображение');
  }

  if (compressed.size > MAX_UPLOAD_BYTES) {
    throw new ImageUploadError('Картинка слишком большая даже после сжатия');
  }

  const dataUrl = await readAsDataUrl(compressed);

  return api<UploadedImage>('/images', { method: 'POST', body: { data: dataUrl, kind } });
}

/**
 * Вложение к заказу. В отличие от обложек кадрировать его нельзя: художник
 * показывает работу целиком. Сжатие остаётся — исходник с планшета весит
 * десятки мегабайт, а до сервера должно доехать полтора.
 */
export async function uploadOrderAttachment(
  orderId: string,
  file: Blob,
  visibility: 'PUBLIC' | 'PRIVATE',
  kind: OrderAttachmentKind = 'OTHER',
): Promise<OrderAttachment> {
  return api<OrderAttachment>(`/orders/${orderId}/attachments`, {
    method: 'POST',
    body: {
      data: await prepareAttachment(file),
      visibility,
      kind,
      ...(isDocument(file) ? { fileName: file instanceof File ? file.name : 'document' } : {}),
    },
  });
}

/**
 * Файл, готовый уехать в теле запроса. Картинка по дороге сжимается: исходник
 * с планшета весит десятки мегабайт, а до сервера должно доехать полтора.
 * Документ уходит байт в байт — сжимать бриф или чек нечем, — поэтому его
 * размер проверяется здесь: пять мегабайт по мобильной сети лучше не гнать
 * впустую, если сервер их всё равно не примет.
 *
 * Отдельно от загрузки потому, что файл нужен не только вложением: чек об
 * оплате уезжает внутри сообщения заказчика, одним запросом с суммой.
 */
export async function prepareAttachment(file: Blob): Promise<string> {
  if (isDocument(file)) {
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new ImageUploadError(
        `Документ больше ${Math.round(MAX_DOCUMENT_BYTES / 1_000_000)} МБ — такой не примем`,
      );
    }

    return readAsDataUrl(file);
  }

  const preset = IMAGE_PRESETS.preview;

  let compressed: Blob;
  try {
    compressed = await imageCompression(toFile(file), {
      maxWidthOrHeight: preset.maxSide,
      maxSizeMB: preset.maxSizeMb,
      fileType: 'image/webp',
      useWebWorker: true,
      preserveExif: false,
    });
  } catch {
    throw new ImageUploadError('Не удалось подготовить изображение');
  }

  if (compressed.size > MAX_UPLOAD_BYTES) {
    throw new ImageUploadError('Картинка слишком большая даже после сжатия');
  }

  return readAsDataUrl(compressed);
}

export function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new ImageUploadError('Не удалось прочитать изображение'));
    reader.readAsDataURL(blob);
  });
}

function toFile(blob: Blob): File {
  return blob instanceof File
    ? blob
    : new File([blob], 'image', { type: blob.type || 'image/png' });
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new ImageUploadError('Не удалось прочитать изображение'));
    image.src = source;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImageUploadError('Пустое изображение'))),
      'image/webp',
      0.92,
    );
  });
}

/**
 * Поворот кадрируется в два шага: сначала повёрнутый исходник целиком, затем из
 * него вырезается выбранная область — иначе углы обрезаются по старой рамке.
 */
function drawRotated(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  area: CropArea,
  rotation: number,
  canvas: HTMLCanvasElement,
): void {
  const radians = (rotation * Math.PI) / 180;
  const rotated = document.createElement('canvas');
  const diagonal = Math.hypot(image.width, image.height);
  rotated.width = diagonal;
  rotated.height = diagonal;

  const rotatedContext = rotated.getContext('2d');
  if (!rotatedContext) {
    throw new ImageUploadError('Браузер не дал обработать изображение');
  }

  rotatedContext.translate(diagonal / 2, diagonal / 2);
  rotatedContext.rotate(radians);
  rotatedContext.drawImage(image, -image.width / 2, -image.height / 2);

  const offsetX = (diagonal - image.width) / 2;
  const offsetY = (diagonal - image.height) / 2;

  context.drawImage(
    rotated,
    area.x + offsetX,
    area.y + offsetY,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
}
